import { useState, useEffect } from "react";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import "./Warehouses.css";

const Warehouses = () => {
  const { showToast } = useToast();
  const { isManagerOrAdmin } = useAuth();

  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [totalWarehouses, setTotalWarehouses] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");

  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [warehouseForm, setWarehouseForm] = useState({
    name: "",
    locationId: ""
  });

  // Room management state
  const [warehouseRooms, setWarehouseRooms] = useState({});
  const [expandedWarehouses, setExpandedWarehouses] = useState(new Set());
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedWarehouseForRoom, setSelectedWarehouseForRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({ roomTitle: "" });
  const [showDeleteRoomConfirm, setShowDeleteRoomConfirm] = useState(false);
  const [deleteRoomTarget, setDeleteRoomTarget] = useState(null);
  const [showMoveTagsModal, setShowMoveTagsModal] = useState(false);
  const [selectedMoveRoomRid, setSelectedMoveRoomRid] = useState(null);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const data = await api.getWarehouses(currentPage, pageLimit);
      setWarehouses(data.warehouses || []);
      setTotalWarehouses(data.totalWarehouses || data.warehouses?.length || 0);
    } catch (error) {
      showToast(error.message || "Failed to fetch warehouses", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations(1, 100);
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchAllRooms = async () => {
    try {
      const data = await api.getRooms({ forTagAssignment: true, limit: 1000 });
      setAllRooms(data.rooms || []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    }
  };

  const createWarehouse = async () => {
    if (!warehouseForm.name) {
      showToast("Warehouse name is required", 2000, true, "danger");
      return;
    }

    if (!warehouseForm.locationId) {
      showToast("Please select a location", 2000, true, "danger");
      return;
    }

    try {
      const payload = {
        name: warehouseForm.name,
        locationId: warehouseForm.locationId
      };
      await api.createWarehouse(payload);
      showToast("Warehouse created successfully!", 2000, true, "success");
      setShowWarehouseModal(false);
      resetWarehouseForm();
      fetchWarehouses();
    } catch (error) {
      showToast(error.message || "Failed to create warehouse", 2000, true, "danger");
      console.error(error);
    }
  };

  const updateWarehouse = async () => {
    if (!warehouseForm.name) {
      showToast("Warehouse name is required", 2000, true, "danger");
      return;
    }

    if (!warehouseForm.locationId) {
      showToast("Please select a location", 2000, true, "danger");
      return;
    }

    try {
      await api.updateWarehouse(editingWarehouse._id, warehouseForm);
      showToast("Warehouse updated successfully!", 2000, true, "success");
      setShowWarehouseModal(false);
      setEditingWarehouse(null);
      resetWarehouseForm();
      fetchWarehouses();
    } catch (error) {
      showToast(error.message || "Failed to update warehouse", 2000, true, "danger");
      console.error(error);
    }
  };

  const deleteWarehouse = async (tagAction = "delete", moveTagsToRid = null) => {
    if (!deleteTarget) return;

    try {
      await api.deleteWarehouse(deleteTarget._id, {
        tagAction,
        moveTagsToRid,
      });
      showToast("Warehouse deleted successfully!", 2000, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchWarehouses();
    } catch (error) {
      showToast(error.message || "Failed to delete warehouse", 2000, true, "danger");
      console.error(error);
    }
  };
  const handleMoveTagsOnWarehouseDelete = () => {
    if (!deleteTarget) return;
    setSelectedMoveRoomRid(null);
    setShowMoveTagsModal(true);
  };

  const confirmMoveTagsOnWarehouseDelete = () => {
    if (!selectedMoveRoomRid) {
      showToast("Please select a target room", 2000, true, "danger");
      return;
    }
    deleteWarehouse("move", selectedMoveRoomRid);
    setShowMoveTagsModal(false);
  };

  const resetWarehouseForm = () => {
    setWarehouseForm({
      name: "",
      locationId: ""
    });
  };

  // Room management functions
  const fetchWarehouseRooms = async (warehouseId) => {
    try {
      const data = await api.getWarehouseRooms(warehouseId);
      setWarehouseRooms(prev => ({
        ...prev,
        [warehouseId]: data.rooms || []
      }));
    } catch (error) {
      console.error(`Failed to fetch rooms for warehouse ${warehouseId}:`, error);
    }
  };

  const toggleWarehouseExpansion = (warehouseId) => {
    const newExpanded = new Set(expandedWarehouses);
    if (newExpanded.has(warehouseId)) {
      newExpanded.delete(warehouseId);
    } else {
      newExpanded.add(warehouseId);
      if (!warehouseRooms[warehouseId]) {
        fetchWarehouseRooms(warehouseId);
      }
    }
    setExpandedWarehouses(newExpanded);
  };

  const createRoom = async () => {
    if (!roomForm.roomTitle.trim()) {
      showToast("Partition title is required", 2000, true, "danger");
      return;
    }

    try {
      await api.createRoom({
        roomTitle: roomForm.roomTitle,
        warehouseId: selectedWarehouseForRoom._id
      });
      showToast("Partition created successfully!", 2000, true, "success");
      setShowRoomModal(false);
      setRoomForm({ roomTitle: "" });
      fetchWarehouseRooms(selectedWarehouseForRoom._id);
    } catch (error) {
      showToast(error.message || "Failed to create room", 2000, true, "danger");
    }
  };

  const updateRoom = async () => {
    if (!roomForm.roomTitle.trim()) {
      showToast("Partition title is required", 2000, true, "danger");
      return;
    }

    try {
      await api.updateRoom(editingRoom.rid, { roomTitle: roomForm.roomTitle });
      showToast("Partition updated successfully!", 2000, true, "success");
      setShowRoomModal(false);
      setEditingRoom(null);
      setRoomForm({ roomTitle: "" });
      if (selectedWarehouseForRoom) {
        fetchWarehouseRooms(selectedWarehouseForRoom._id);
      }
    } catch (error) {
      showToast(error.message || "Failed to update room", 2000, true, "danger");
    }
  };

  const deleteRoom = async (deleteTags = false) => {
    if (!deleteRoomTarget) return;

    try {
      await api.deleteRoom(deleteRoomTarget.rid, deleteTags);
      showToast("Partition deleted successfully!", 2000, true, "success");
      setShowDeleteRoomConfirm(false);
      setDeleteRoomTarget(null);
      if (selectedWarehouseForRoom) {
        fetchWarehouseRooms(selectedWarehouseForRoom._id);
      }
    } catch (error) {
      showToast(error.message || "Failed to delete room", 2000, true, "danger");
    }
  };

  useEffect(() => {
    fetchWarehouses();
    fetchLocations();
    fetchAllRooms();
  }, []);

  useEffect(() => {
    fetchWarehouses();
  }, [currentPage]);

  const filteredWarehouses = warehouses.filter(warehouse => {
    const matchesSearch = warehouse.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = filterLocation === "all" ||
      (warehouse.locationId?._id || warehouse.locationId) === filterLocation;
    return matchesSearch && matchesLocation;
  });

  const totalPages = Math.ceil(totalWarehouses / pageLimit);

  const getLocationName = (locationId) => {
    if (!locationId) return "Unassigned";
    // Use populated location data directly if available
    if (typeof locationId === 'object' && locationId.name) {
      return locationId.name;
    }
    // Fallback to looking up in locations array
    const loc = locations.find(l => l._id === (locationId._id || locationId));
    return loc?.name || "Unknown";
  };

  const getLocationAddress = (locationId) => {
    if (!locationId) return null;
    if (typeof locationId === 'object' && locationId.address) {
      return locationId.address;
    }
    const loc = locations.find(l => l._id === (locationId._id || locationId));
    return loc?.address || null;
  };

  const locationOptions = [
    { label: "All Locations", value: "all" },
    { label: "Unassigned", value: "unassigned" },
    ...locations.map(loc => ({ label: loc.name, value: loc._id }))
  ];

  const locationFormOptions = locations.map(loc => ({ label: loc.name, value: loc._id }));

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Warehouses</h1>
            <p className="dashboard-subtitle">Manage warehouses and their tag assignments</p>
          </div>
          <div className="dfcc gap-2">
            {isManagerOrAdmin() && (
              <Button
                value="Add Warehouse"
                iconLeft="fa-plus"
                onClick={() => {
                  setEditingWarehouse(null);
                  resetWarehouseForm();
                  setShowWarehouseModal(true);
                }}
                variant="primary"
                padding="12px 24px"
              />
            )}
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#fff3e0" }}>
              <i className="fa fa-building" style={{ color: "#FF9800" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{totalWarehouses}</h3>
              <p className="stat-label">Total Warehouses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e0e7ff" }}>
              <i className="fa fa-cube" style={{ color: "#6366f1" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {warehouses.reduce((acc, w) => acc + (w.rooms?.length || 0), 0)}
              </h3>
              <p className="stat-label">Total Partitions</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e8f5e9" }}>
              <i className="fa fa-link" style={{ color: "#4CAF50" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {warehouses.filter(w => w.locationId).length}
              </h3>
              <p className="stat-label">With Location</p>
            </div>
          </div>

         
        </div>

        <div className="filters-section">
          <h2 className="section-title">Search & Filter Warehouses</h2>
          <div className="filters-grid">
            <TextField
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconLeft="fa-search"
              variant="outlined"
            />
            <Dropdown
              options={locationOptions}
              value={filterLocation}
              onChange={setFilterLocation}
              placeholder="Select Location"
              variant="outlined"
            />
            <Button
              value="Clear"
              iconLeft="fa-times"
              onClick={() => {
                setSearchTerm("");
                setFilterLocation("all");
              }}
              variant="outline"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="rooms-section">
          <h2 className="section-title">Warehouses</h2>
          {/* `warehouses-grid` stops the cards stretching to a uniform row
              height — see Warehouses.css. Only this grid needs it, because
              only these cards grow when their partition list is shown. */}
          <div className="rooms-grid warehouses-grid">
            {loading ? (
              <div className="loading-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#FF9800" }}></i>
                <p>Loading warehouses...</p>
              </div>
            ) : filteredWarehouses.length === 0 ? (
              <div className="empty-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-building" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p>No warehouses found</p>
              </div>
            ) : (
              filteredWarehouses.map((warehouse) => (
                <div key={warehouse._id} className="room-card">
                  <div className="room-header">
                    <div className="room-icon" style={{
                      background: "linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%)",
                      color: "#E65100"
                    }}>
                      <i className="fa fa-building"></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 className="room-title">{warehouse.name}</h3>
                      <div className="room-id" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <i className="fa fa-map-marker" style={{ marginRight: "6px", color: "#4CAF50" }}></i>
                          <strong style={{ color: "#0F172A" }}>{getLocationName(warehouse.locationId)}</strong>
                        </span>
                        {getLocationAddress(warehouse.locationId) && (
                          <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "18px" }}>
                            {getLocationAddress(warehouse.locationId)}
                          </span>
                        )}
                      </div>
                    </div>
                    {isManagerOrAdmin() && (
                      <div className="room-actions">
                        <button
                          className="room-action-btn edit"
                          onClick={() => {
                            setEditingWarehouse(warehouse);
                            setWarehouseForm({
                              name: warehouse.name,
                              locationId: warehouse.locationId?._id || warehouse.locationId || ""
                            });
                            setShowWarehouseModal(true);
                          }}
                          title="Edit Warehouse"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        <button
                          className="room-action-btn delete"
                          onClick={() => {
                            setDeleteTarget(warehouse);
                            setShowDeleteConfirm(true);
                          }}
                          title="Delete Warehouse"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="room-stats">
                    <div className="room-stat">
                      <i className="fa fa-cube"></i>
                      <span>{warehouseRooms[warehouse._id]?.length || warehouse.rooms?.length || 0} Partitions</span>
                    </div>
                  </div>

                  <Button
                    value={expandedWarehouses.has(warehouse._id) ? "Hide Partitions" : "Show Partitions"}
                    iconLeft={expandedWarehouses.has(warehouse._id) ? "fa-chevron-up" : "fa-chevron-down"}
                    onClick={() => toggleWarehouseExpansion(warehouse._id)}
                    variant="outline"
                    width="100%"
                    padding="10px 16px"
                    style={{ marginBottom: "12px" }}
                  />

                  {expandedWarehouses.has(warehouse._id) && (
                    <div style={{
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      padding: "16px",
                      marginTop: "8px",
                      border: "1px solid #e2e8f0"
                    }}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px"
                      }}>
                        <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#0F172A" }}>
                          Partitions ({warehouseRooms[warehouse._id]?.length || 0})
                        </h4>
                        {isManagerOrAdmin() && (
                          <button
                            onClick={() => {
                              setSelectedWarehouseForRoom(warehouse);
                              setEditingRoom(null);
                              setRoomForm({ roomTitle: "" });
                              setShowRoomModal(true);
                            }}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              backgroundColor: "#dbeafe",
                              color: "#3b82f6",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px"
                            }}
                          >
                            <i className="fa fa-plus"></i> Add Partition
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {!warehouseRooms[warehouse._id] ? (
                          <p style={{ color: "#94a3b8", fontSize: "14px", textAlign: "center", margin: "12px 0" }}>
                            <i className="fa fa-spinner fa-spin"></i> Loading partitions...
                          </p>
                        ) : warehouseRooms[warehouse._id].length === 0 ? (
                          <p style={{ color: "#94a3b8", fontSize: "14px", textAlign: "center", margin: "12px 0" }}>
                            No partitions in this warehouse
                          </p>
                        ) : (
                          warehouseRooms[warehouse._id].map(room => (
                            <div key={room._id} style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0"
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <i className="fa fa-door-open" style={{ color: "#6366f1" }}></i>
                                <div>
                                  <span style={{ fontWeight: "600", color: "#0F172A" }}>{room.roomTitle}</span>
                                  <span style={{ marginLeft: "8px", color: "#94a3b8", fontSize: "12px" }}>
                                    (ID: {room.rid})
                                  </span>
                                </div>
                              </div>
                              {isManagerOrAdmin() && (
                                <div style={{ display: "flex", gap: "4px" }}>
                                  <button
                                    onClick={() => {
                                      setSelectedWarehouseForRoom(warehouse);
                                      setEditingRoom(room);
                                      setRoomForm({ roomTitle: room.roomTitle });
                                      setShowRoomModal(true);
                                    }}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "none",
                                      backgroundColor: "#f0fdf4",
                                      color: "#22c55e",
                                      cursor: "pointer"
                                    }}
                                    title="Edit Partition"
                                  >
                                    <i className="fa fa-edit"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedWarehouseForRoom(warehouse);
                                      setDeleteRoomTarget(room);
                                      setShowDeleteRoomConfirm(true);
                                    }}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "none",
                                      backgroundColor: "#fef2f2",
                                      color: "#ef4444",
                                      cursor: "pointer"
                                    }}
                                    title="Delete Partition"
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {!loading && totalWarehouses > pageLimit && (
          <div className="pagination" style={{ background: "white", padding: "16px 24px", borderRadius: "16px", border: "1px solid #E2E8F0" }}>
            <Button
              value="Previous"
              iconLeft="fa-chevron-left"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              variant="outline"
              padding="8px 16px"
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <Button
              value="Next"
              iconRight="fa-chevron-right"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              variant="outline"
              padding="8px 16px"
            />
          </div>
        )}
      </div>

      {showWarehouseModal && (
        <div className="modal-overlay" onClick={() => setShowWarehouseModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%)", color: "#E65100" }}>
                <i className="fa fa-building"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingWarehouse ? "Edit Warehouse" : "Add New Warehouse"}</span>
                <span className="modal-subtitle">
                  {editingWarehouse ? "Update warehouse information" : "Create a new warehouse"}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowWarehouseModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Warehouse Name</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Enter warehouse name"
                  value={warehouseForm.name}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, name: e.target.value })}
                  variant="outlined"
                />
              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Location</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={locationFormOptions}
                  value={warehouseForm.locationId}
                  onChange={(val) => setWarehouseForm({ ...warehouseForm, locationId: val })}
                  placeholder="Select a location"
                  variant="outlined"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowWarehouseModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingWarehouse ? updateWarehouse : createWarehouse}
              >
                <i className={`fa ${editingWarehouse ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingWarehouse ? "Update Warehouse" : "Create Warehouse"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete Warehouse</span>
                <span className="modal-subtitle delete-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-building"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    This will delete the warehouse and all its rooms. Choose what to do with tags in those rooms.
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={() => deleteWarehouse("unassign")}
              >
                <i className="fa fa-unlink"></i>
                <span>Delete warehouse (unassign tags)</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={handleMoveTagsOnWarehouseDelete}
              >
                <i className="fa fa-arrow-right"></i>
                <span>Move all tags to another room</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={() => deleteWarehouse("delete")}
              >
                <i className="fa fa-trash"></i>
                <span>Delete warehouse and all tags</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMoveTagsModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowMoveTagsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle">
                <i className="fa fa-arrow-right"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Move Tags to Another Partition</span>
                <span className="modal-subtitle">
                  Select a target room for all tags under warehouse "{deleteTarget.name}"
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowMoveTagsModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Target Partition</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={[
                    { label: "Select a room", value: "" },
                    ...allRooms
                      .filter(r => {
                        const whId = r.warehouseId?._id || r.warehouseId;
                        return whId !== (deleteTarget._id || deleteTarget.id);
                      })
                      .map(r => {
                        const warehouse = warehouses.find(w => (w._id || w.id) === (r.warehouseId?._id || r.warehouseId));
                        const location = locations.find(l => (l._id || l.id) === (r.locationId?._id || r.locationId));
                        const warehouseName = warehouse?.name || "Unknown warehouse";
                        const locationName = location?.name || "Unknown location";
                        return {
                          value: r.rid,
                          label: `${r.roomTitle} (RID ${r.rid}) — ${warehouseName} / ${locationName}`,
                        };
                      }),
                  ]}
                  value={selectedMoveRoomRid || ""}
                  onChange={(val) => setSelectedMoveRoomRid(val || null)}
                  placeholder="Select a room"
                  size="md"
                  width="100%"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => setShowMoveTagsModal(false)}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={confirmMoveTagsOnWarehouseDelete}
              >
                <i className="fa fa-check"></i>
                <span>Move Tags</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRoomModal && selectedWarehouseForRoom && (
        <div className="modal-overlay" onClick={() => setShowRoomModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)", color: "#4338CA" }}>
                <i className="fa fa-door-open"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingRoom ? "Edit Partition" : "Add New Partition"}</span>
                <span className="modal-subtitle">
                  {editingRoom ? "Update room information" : `Create a new room in ${selectedWarehouseForRoom.name}`}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowRoomModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Partition Title</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Enter room title"
                  value={roomForm.roomTitle}
                  onChange={(e) => setRoomForm({ ...roomForm, roomTitle: e.target.value })}
                  variant="outlined"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowRoomModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingRoom ? updateRoom : createRoom}
              >
                <i className={`fa ${editingRoom ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingRoom ? "Update Partition" : "Create Partition"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteRoomConfirm && deleteRoomTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteRoomConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete Partition</span>
                <span className="modal-subtitle delete-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteRoomConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-door-open"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteRoomTarget.roomTitle}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    {(deleteRoomTarget.tags?.length ?? 0) > 0
                      ? `This room contains ${deleteRoomTarget.tags.length} tag(s). Do you want to delete them as well?`
                      : "This will remove the room and unassign any associated tags."}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="modal-footer-wrapper"
              style={(deleteRoomTarget.tags?.length ?? 0) > 0 ? { flexDirection: "column", alignItems: "stretch" } : {}}
            >
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteRoomConfirm(false);
                  setDeleteRoomTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              {(deleteRoomTarget.tags?.length ?? 0) > 0 ? (
                <>
                  <div
                    className="modal-action-btn delete-btn"
                    onClick={() => deleteRoom(false)}
                  >
                    <i className="fa fa-unlink"></i>
                    <span>Delete room only (unassign tags)</span>
                  </div>
                  <div
                    className="modal-action-btn delete-btn"
                    onClick={() => deleteRoom(true)}
                  >
                    <i className="fa fa-trash"></i>
                    <span>Delete room and tags</span>
                  </div>
                </>
              ) : (
                <div
                  className="modal-action-btn delete-btn"
                  onClick={() => deleteRoom(false)}
                >
                  <i className="fa fa-trash"></i>
                  <span>Delete Partition</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Warehouses;
